import type { Dispatch } from 'redux';

import type {
  GetPreferenceApiV1UserPreferencesContextKeyGetApiResponse,
  GetSelfApiV1UserSelfGetApiResponse,
  JsonValueInput,
  SetUserPreferenceRequest,
  UpdateWorkdayUserRequest,
  WorkdayEnvironmentNode,
} from '~/api/rolodex/typedefs.autogen';
import type { ServiceRequestConfig } from '~/services/createService';

import { endpoints as autogenEnhancements } from '~/api/rolodex/enhancements.autogen';
import { Tags } from '~/api/rolodex/tags.autogen';
import { api as clientApi } from '~/redux/api/createApi';
import { TagType } from '~/redux/api/TagType';

import { rolodexApiRoot } from './root';

type WorkdayEnvironments = Record<string, WorkdayEnvironmentNode>;

type WorkdayTenantEnvironmentParams = {
  tenantId: string;
  tenantEnvironment: string;
  cpsHost?: string | null;
  galahadHost?: string | null;
  suvId?: string | null;
};

type RefreshWorkdayClientHostsParams = {
  pilotClientId: number;
  cpsHost?: string | null;
  galahadHost?: string | null;
  suvId?: string | null;
};

type RefreshWorkdayClientRegistrationParams = {
  pilotClientId: number;
};

type RefreshWorkdayClientRegistrationResponse = {
  detail: string;
};

type UpdateWorkdayUserParams = {
  userId: string | number;
  body: {
    roleUuid: string;
    pilotDepartmentIds: number[];
  };
};

type CurrentUserAttributes = {
  user_management: 'workday' | 'evisort';
};

type CurrentUserResponse = {
  data: Array<{ attributes: CurrentUserAttributes }>;
};

type CancelClientDeletionLifecycleApi = {
  dispatch: Dispatch;
  queryFulfilled: Promise<unknown>;
};

async function invalidateClientsAfterCancellation(
  _arg: { clientId: number },
  { dispatch, queryFulfilled }: CancelClientDeletionLifecycleApi,
) {
  try {
    await queryFulfilled;
    dispatch(
      clientApi.util.invalidateTags([{ type: TagType.Clients, id: 'LIST' }]),
    );
  } catch {
    // The base query owns the error toast.
  }
}

/**
 * A preference is addressed by a context from a closed set plus an open key,
 * and holds any JSON value. Every preference here lives in `GLOBAL`; none are
 * scoped to a folder or search. `useUserPreferences` (`./useUserPreferences`)
 * owns the keys and how to parse each value, except `homeFirstRun`, which
 * `useHomeFirstRun` reads directly because it has to wait on the read and
 * stay closed when it fails. This file only reads and writes a key generically.
 */
const PREFERENCE_CONTEXT_GLOBAL = 'GLOBAL';

export type UserPreferenceKey =
  | 'pinnedNavItems'
  | 'sidebarNavOptIn'
  | 'sidebarNavCollapsed'
  | 'detailsPanelPreferences'
  | 'homeFirstRun';

/**
 * A key alone reads with the service defaults. The object form adds request
 * options, such as `showErrorToast: false` for a background read nobody
 * needs told about. Each form is its own cache entry, so a caller that
 * patches the cache has to use the same form it reads with.
 */
export type UserPreferenceQueryArg =
  | UserPreferenceKey
  | { key: UserPreferenceKey; rtkQueryOptions?: ServiceRequestConfig };

function preferenceKey(arg: UserPreferenceQueryArg) {
  return typeof arg === 'string' ? arg : arg.key;
}

function preferenceUrl(key: UserPreferenceKey) {
  return `/api/v1/user/preferences/${PREFERENCE_CONTEXT_GLOBAL}/${key}`;
}

function mapWorkdayEnvironments(
  environments: WorkdayEnvironmentNode[],
): WorkdayEnvironments {
  return environments.reduce<WorkdayEnvironments>((result, environment) => {
    result[environment.environment] = environment;
    return result;
  }, {});
}

rolodexApiRoot.enhanceEndpoints({ endpoints: autogenEnhancements });

rolodexApiRoot.enhanceEndpoints({
  endpoints: {
    cancelClientDeletionApiV1ClientDeletionClientIdDelete: {
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        arg: { clientId: number },
      ) => [{ type: Tags.client_deletion, id: arg.clientId }],
      onQueryStarted: invalidateClientsAfterCancellation,
    },
  },
});

export const rolodexApi = rolodexApiRoot.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    getWorkdayEnvironments: build.query<WorkdayEnvironments, void>({
      query: () => ({
        url: '/api/v1/client-mapping/workday/environments',
        method: 'get',
      }),
      transformResponse: (response: WorkdayEnvironmentNode[]) =>
        mapWorkdayEnvironments(response),
      providesTags: [{ type: Tags.client_mapping, id: 'workday-environments' }],
    }),

    getWorkdayTenantEnvironment: build.query<
      WorkdayEnvironmentNode,
      WorkdayTenantEnvironmentParams
    >({
      query: ({
        tenantId,
        tenantEnvironment,
        cpsHost,
        galahadHost,
        suvId,
      }) => ({
        url: `/api/v1/client-mapping/workday/environments/${encodeURIComponent(tenantId)}/${encodeURIComponent(tenantEnvironment)}`,
        method: 'get',
        params: {
          cps_host: cpsHost,
          galahad_host: galahadHost,
          suv_id: suvId,
        },
      }),
    }),

    refreshWorkdayClientHosts: build.mutation<
      WorkdayEnvironmentNode,
      RefreshWorkdayClientHostsParams
    >({
      query: ({ pilotClientId, cpsHost, galahadHost, suvId }) => ({
        url: `/api/v1/client-mapping/${pilotClientId}/workday/refresh-hosts`,
        method: 'post',
        body: {},
        params: {
          cps_host: cpsHost,
          galahad_host: galahadHost,
          suv_id: suvId,
        },
      }),
      invalidatesTags: [
        { type: Tags.client_mapping, id: 'workday-environments' },
      ],
    }),

    refreshWorkdayClientRegistration: build.mutation<
      RefreshWorkdayClientRegistrationResponse,
      RefreshWorkdayClientRegistrationParams
    >({
      query: ({ pilotClientId }) => ({
        url: `/api/v1/client-mapping/${pilotClientId}/workday/refresh-registration`,
        method: 'post',
        body: {},
      }),
    }),

    updateWorkdayUser: build.mutation<void, UpdateWorkdayUserParams>({
      query: ({ userId, body }) => ({
        url: `/api/v1/user/workday/${userId}`,
        method: 'put',
        body: {
          role_uuid: body.roleUuid,
          pilot_department_ids: body.pilotDepartmentIds,
        } satisfies UpdateWorkdayUserRequest,
      }),
      invalidatesTags: [{ type: Tags.user, id: 'LIST' }],
    }),

    getUserPreference: build.query<unknown, UserPreferenceQueryArg>({
      query: (arg) => ({
        url: preferenceUrl(preferenceKey(arg)),
        method: 'get',
        rtkQueryOptions:
          typeof arg === 'string' ? undefined : arg.rtkQueryOptions,
      }),
      // An unset preference is a 204 with no body, and an older shape of a
      // feature may have written a differently shaped value. Callers parse
      // and validate the raw value themselves.
      transformResponse: (
        response: GetPreferenceApiV1UserPreferencesContextKeyGetApiResponse,
        _meta,
        arg,
      ) => response?.attributes?.[preferenceKey(arg)],
      providesTags: (_result, _error, arg) => [
        { type: Tags.user_preferences, id: preferenceKey(arg) },
      ],
    }),

    setUserPreference: build.mutation<
      void,
      {
        key: UserPreferenceKey;
        value: JsonValueInput;
        rtkQueryOptions?: ServiceRequestConfig;
      }
    >({
      query: ({ key, value, rtkQueryOptions }) => ({
        url: preferenceUrl(key),
        method: 'put',
        body: {
          type: 'user-preference',
          attributes: { value },
        } satisfies SetUserPreferenceRequest,
        rtkQueryOptions,
      }),
      // Callers patch the cache optimistically and batch their writes, so
      // success tells us nothing new. A failure means the cache is ahead of
      // what's stored; reset it to what a reload would show. The service
      // toasts the error unless the caller turned it off.
      invalidatesTags: (_result, error, { key }) =>
        error ? [{ type: Tags.user_preferences, id: key }] : [],
    }),

    getIsWorkdayUser: build.query<boolean, void>({
      query: () => ({ url: '/api/v1/user/self', method: 'get' }),
      transformResponse: (response: GetSelfApiV1UserSelfGetApiResponse) => {
        const currentUser = response as unknown as CurrentUserResponse;
        return currentUser.data[0]?.attributes.user_management === 'workday';
      },
      providesTags: [{ type: Tags.user, id: 'self' }],
    }),
  }),
});
